import { Injectable, inject, type OnDestroy } from '@angular/core';

import { AuthService } from '@auth0/auth0-angular';

import * as signalR from '@microsoft/signalr';

import { BehaviorSubject, Subject, type Observable, firstValueFrom } from 'rxjs';

import { TenantOverrideService } from '@core/services/tenant-override.service';

import { environment } from '@environments/environment';

export type ConnectionState = 'disconnected' | 'connecting' | 'connected' | 'error';

/**
 * Manages the Azure SignalR connection lifecycle. Ported from Chroniq/Mobile-Client's
 * signalr.service.ts — same hub, same wire contract (SignalRFunctions.BroadcastEvent
 * broadcasts message name "event" to the caller's tenant group), no Capacitor/native
 * concerns here so this ports essentially unchanged.
 *
 * Connection flow:
 *  1. Request Access Token from Auth0
 *  2. `POST /api/signalr/negotiate?access_token=<token>` → receive SignalR URL + token
 *  3. Start HubConnection — negotiate binds the connection to the user and adds it to its TenantId group (chat events go to room members only)
 *
 * Consumers should inject EventBusService to subscribe to typed event streams.
 * This service is responsible only for connection management.
 */
@Injectable({ providedIn: 'root' })
export class SignalRService implements OnDestroy {
  private readonly auth0 = inject(AuthService);
  private readonly tenantOverride = inject(TenantOverrideService);

  private connection: signalR.HubConnection | null = null;

  /** In-flight connect, shared so concurrent callers don't build a second connection. */
  private connectPromise: Promise<void> | null = null;

  private readonly _state$ = new BehaviorSubject<ConnectionState>('disconnected');
  readonly state$: Observable<ConnectionState> = this._state$.asObservable();

  /**
   * Fires after the realtime connection re-establishes following a drop — NOT on the
   * initial connect. Entity stores (core/state/entity-store.ts) resync from the server on
   * this signal to recover events missed during the gap; firing it on first connect too
   * would just duplicate the store's own initial `load()`.
   */
  private readonly _reconnected$ = new Subject<void>();
  readonly reconnected$: Observable<void> = this._reconnected$.asObservable();

  /** Raw message handler registered by EventBusService. */
  private onMessageHandler: ((data: unknown) => void) | null = null;

  // ─── Lifecycle ──────────────────────────────────────────────────────────────

  async connect(): Promise<void> {
    if (this.connection?.state === signalR.HubConnectionState.Connected) {
      return;
    }
    // Concurrent callers during the await gap must share one connect, or every event
    // would be dispatched twice.
    if (this.connectPromise) {
      return this.connectPromise;
    }
    // Automatic reconnect already in progress (no in-flight connect of ours) — don't build a second.
    if (
      this.connection?.state === signalR.HubConnectionState.Connecting ||
      this.connection?.state === signalR.HubConnectionState.Reconnecting
    ) {
      return;
    }

    this.connectPromise = this.openConnection();
    try {
      await this.connectPromise;
    } finally {
      this.connectPromise = null;
    }
  }

  /**
   * Manual retry after the automatic reconnect gave up (or the first connect failed). Entity
   * stores resync on `reconnected$`, so a successful retry also refreshes what was missed.
   */
  async reconnect(): Promise<void> {
    await this.connect();
    this._reconnected$.next();
  }

  private async openConnection(): Promise<void> {
    this._state$.next('connecting');
    const tenantOverrideId = this.tenantOverride.activeTenantId();

    try {
      this.connection = new signalR.HubConnectionBuilder()
        .withUrl(environment.signalRUrl, {
          // Auth0 token passed as query param – negotiation endpoint strips it and
          // validates via OIDC middleware before returning SignalR token. Fetch a fresh
          // token on every (re)connect so automatic reconnects never use an expired one.
          accessTokenFactory: async () => {
            const token = await firstValueFrom(this.auth0.getAccessTokenSilently());
            if (!token) {throw new Error('No Auth0 access token available for SignalR negotiate.');}
            return token;
          },
          // No cookie-based auth is used (Bearer token only) — disabling credentials
          // avoids requiring Access-Control-Allow-Credentials on the Function App's CORS config.
          withCredentials: false,
          // Mirrors adminTenantOverrideInterceptor for plain HTTP calls — SignalRAuthMiddleware
          // honours the same header so an impersonating admin joins the right tenant's group.
          ...(tenantOverrideId ? { headers: { 'X-Admin-TenantId': tenantOverrideId } } : {}),
        })
        .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
        .configureLogging(
          environment.production ? signalR.LogLevel.Warning : signalR.LogLevel.Information,
        )
        .build();

      // Register event listener before starting the connection
      this.connection.on('event', (data: unknown) => {
        this.onMessageHandler?.(data);
      });

      this.connection.onreconnecting(() => this._state$.next('connecting'));
      this.connection.onreconnected(() => {
        this._state$.next('connected');
        this._reconnected$.next();
      });
      this.connection.onclose(() => this._state$.next('disconnected'));

      await this.connection.start();
      this._state$.next('connected');
    } catch (err) {
      this._state$.next('error');
      console.error('[SignalR] Connection failed:', err);
      throw err;
    }
  }

  async disconnect(): Promise<void> {
    if (this.connection) {
      await this.connection.stop();
      this.connection = null;
    }
    this._state$.next('disconnected');
  }

  /** Called once by EventBusService to register its message dispatch handler. */
  registerMessageHandler(handler: (data: unknown) => void): void {
    this.onMessageHandler = handler;
  }

  ngOnDestroy(): void {
    void this.disconnect();
  }
}
